import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios, { type AxiosError } from 'axios';
import { type RootState } from '../store';
import { type IVerticalNavLinkTab } from '../components/nav/VerticalNav';
import { getConfig } from '../config/config-helper';
import { type IChildAccount } from '../interfaces/IChildAccount';

export interface IAttendee {
  name: string;
  email: string;
  role: 'admin' | 'consumer' | 'instructor';
}

export interface IAccountPool {
  id: number;
  status: string;
  pool_size: number;
  total_child_accounts: number;
  total_ready_child_accounts: number;
  total_allocated_child_accounts: number;
}

export interface IEventMilestone {
  id: number;
  title: string;
  attendees_reached: number;
  condition_sql: string;
  event: number;
  sequence_order: number;
  expected_by_timestamp: string;
  expected_by_minute: number;
}

export interface IEvent {
  id: number;
  slug: string;
  name: string;
  location: string;
  build_datetime: string;
  start_datetime: string;
  end_datetime: string;
  decommission_datetime: string;
  instructions: string;
  organization_account: string;
  initial_pool_size: number;
  requestor: string | null;
  project: string;
  snowflake_account_edition: string;
  snowflake_account_region_group: string;
  approval_status: 'PENDING' | 'APPROVED' | 'REJECTED';
  approval_date: string | null;
  rejection_reason: string;
  instructor_reconfigure: boolean;
  instructors: string[];
  attendees: IAttendee[];
  account_pool: IAccountPool | null;
  milestones: IEventMilestone[];
  eventAttendees: IEventAttendee[];
  allowed_email_domains?: string[];
}

export interface IMilestoneProgress {
  attendee_id: number;
  event_milestone_id: number;
  achieved_by: string;
}

export interface IEventAttendee {
  email: string;
  name: string;
  account_identifier: string | '';
  account_url: string | '';
  allocated_at: string | '';
  milestones: IMilestoneProgress[];
}

interface IEventsState {
  events: IEvent[];
  tabs: IVerticalNavLinkTab[];
  requestStatus: 'idle' | 'loading' | 'failed' | 'succeeded';
  paginatedEvents: IEvent[];
  paginatedTotal: number;
  paginatedPage: number;
  paginatedTotalPages: number;
  paginatedRequestStatus: 'idle' | 'loading' | 'loading-more' | 'failed' | 'succeeded';
}

const initialState: IEventsState = {
  events: [],
  tabs: [],
  requestStatus: 'idle',
  paginatedEvents: [],
  paginatedTotal: 0,
  paginatedPage: 1,
  paginatedTotalPages: 1,
  paginatedRequestStatus: 'idle',
};

export const fetchEvents = createAsyncThunk(
  'events/fetch',
  async ({ token, role }: { token: string; role: 'admin' | 'consumer' }): Promise<IEvent[]> => {
    const { dataopsAdminAppApiEndpoint, snowflakeOrganizationAccounts } = getConfig();

    const organizationAccount =
      snowflakeOrganizationAccounts && snowflakeOrganizationAccounts.length > 0
        ? snowflakeOrganizationAccounts[0]
        : undefined;

    const url = `${dataopsAdminAppApiEndpoint}/event_management/events`;
    console.log('Fetching events:', url);

    try {
      if (!token) throw new Error('Token not provided');

      const params: Record<string, string> = {};
      if (organizationAccount) {
        params.organization_account = organizationAccount;
      }

      const response = await axios.get(`${url}`, {
        headers: { Authorization: `Bearer ${token}`, 'SSC-Role': role },
        params,
      });

      return response.data;
    } catch (error) {
      const errorMessage = (error as AxiosError).response?.data || 'Unknown error';
      console.error('Error fetching events:', errorMessage);
      throw error;
    }
  },
);

export const fetchEvent = createAsyncThunk(
  'events/fetchOne',
  async ({ token, role, slug }: { token: string; role: string; slug: string }): Promise<IEvent> => {
    const { dataopsAdminAppApiEndpoint } = getConfig();
    const url = `${dataopsAdminAppApiEndpoint}/event_management/events/${slug}`;

    try {
      if (!token) throw new Error('Token not provided');

      const response = await axios.get(`${url}`, {
        headers: { Authorization: `Bearer ${token}`, 'SSC-Role': role },
      });

      return response.data;
    } catch (error) {
      const errorMessage = (error as AxiosError).response?.data || 'Unknown error';
      console.error('Error fetching event:', errorMessage);
      throw error;
    }
  },
);

export const fetchEventDetails = createAsyncThunk(
  'events/fetchDetails',
  async ({ token, role, slug }: { token: string; role: string; slug: string }): Promise<IEvent> => {
    const { dataopsAdminAppApiEndpoint } = getConfig();
    const url = `${dataopsAdminAppApiEndpoint}/event_management/events/${slug}/details`;

    try {
      if (!token) throw new Error('Token not provided');

      const response = await axios.get(`${url}`, {
        headers: { Authorization: `Bearer ${token}`, 'SSC-Role': role },
      });

      return response.data;
    } catch (error) {
      const errorMessage = (error as AxiosError).response?.data || 'Unknown error';
      console.error('Error fetching event details:', errorMessage);
      throw error;
    }
  },
);

export interface IEventAccountsResponse {
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  accounts: IChildAccount[];
}

export const fetchEventAccounts = createAsyncThunk(
  'events/fetchAccounts',
  async ({
    token,
    role,
    slug,
    page = 1,
    pageSize = 50,
    search,
  }: {
    token: string;
    role: string;
    slug: string;
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<IEventAccountsResponse & { slug: string }> => {
    const { dataopsAdminAppApiEndpoint } = getConfig();
    const url = `${dataopsAdminAppApiEndpoint}/event_management/events/${slug}/accounts`;

    try {
      if (!token) throw new Error('Token not provided');

      const params: Record<string, string | number> = {
        page,
        page_size: pageSize,
      };

      if (search) {
        params.search = search;
      }

      const response = await axios.get(`${url}`, {
        headers: { Authorization: `Bearer ${token}`, 'SSC-Role': role },
        params,
      });

      return { ...response.data, slug };
    } catch (error) {
      const errorMessage = (error as AxiosError).response?.data || 'Unknown error';
      console.error('Error fetching event accounts:', errorMessage);
      throw error;
    }
  },
);

export const fetchAllEventAccounts = createAsyncThunk(
  'events/fetchAllAccounts',
  async ({
    token,
    role,
    slug,
    pageSize = 100,
    search,
  }: {
    token: string;
    role: string;
    slug: string;
    pageSize?: number;
    search?: string;
  }): Promise<{ accounts: IChildAccount[]; slug: string }> => {
    const { dataopsAdminAppApiEndpoint } = getConfig();
    const url = `${dataopsAdminAppApiEndpoint}/event_management/events/${slug}/accounts`;

    try {
      if (!token) throw new Error('Token not provided');

      const params: Record<string, string | number> = {
        page: 1,
        page_size: pageSize,
      };

      if (search) {
        params.search = search;
      }

      // Fetch the first page to get total_pages
      const firstResponse = await axios.get(`${url}`, {
        headers: { Authorization: `Bearer ${token}`, 'SSC-Role': role },
        params,
      });

      const firstPageData = firstResponse.data as IEventAccountsResponse;
      const allAccounts = [...firstPageData.accounts];
      const totalPages = firstPageData.total_pages;

      // If there are more pages, fetch them all
      if (totalPages > 1) {
        const pagePromises = [];
        for (let page = 2; page <= totalPages; page++) {
          const pageParams = { ...params, page };
          pagePromises.push(
            axios.get(`${url}`, {
              headers: { Authorization: `Bearer ${token}`, 'SSC-Role': role },
              params: pageParams,
            }),
          );
        }

        // Wait for all pages to complete
        const pageResponses = await Promise.all(pagePromises);

        // Combine all accounts
        pageResponses.forEach((response) => {
          allAccounts.push(...response.data.accounts);
        });
      }

      return {
        accounts: allAccounts,
        slug,
      };
    } catch (error) {
      const errorMessage = (error as AxiosError).response?.data || 'Unknown error';
      console.error('Error fetching all event accounts:', errorMessage);
      throw error;
    }
  },
);

export const fetchSingleEventAccount = createAsyncThunk(
  'events/fetchSingleAccount',
  async ({
    token,
    role,
    slug,
    accountId,
  }: {
    token: string;
    role: string;
    slug: string;
    accountId: number;
  }): Promise<{ account: IChildAccount; slug: string }> => {
    const { dataopsAdminAppApiEndpoint } = getConfig();
    const url = `${dataopsAdminAppApiEndpoint}/event_management/events/${slug}/accounts/${accountId}`;

    try {
      if (!token) throw new Error('Token not provided');

      const response = await axios.get(`${url}`, {
        headers: { Authorization: `Bearer ${token}`, 'SSC-Role': role },
      });

      return {
        account: response.data,
        slug,
      };
    } catch (error) {
      const errorMessage = (error as AxiosError).response?.data || 'Unknown error';
      console.error('Error fetching single event account:', errorMessage);
      throw error;
    }
  },
);

export const fetchEventAttendees = createAsyncThunk(
  'events/fetchEventAttendees',
  async ({ token, role, slug }: { token: string; role: string; slug: string }): Promise<IEventAttendee[]> => {
    const { dataopsAdminAppApiEndpoint } = getConfig();
    const url = `${dataopsAdminAppApiEndpoint}/event_management/${slug}/insights`;

    try {
      if (!token) throw new Error('Token not provided');

      const response = await axios.get(`${url}`, {
        headers: { Authorization: `Bearer ${token}`, 'SSC-Role': role },
      });

      return response.data;
    } catch (error) {
      const errorMessage = (error as AxiosError).response?.data || 'Unknown error';
      console.error('Error fetching event insights:', errorMessage);
      throw error;
    }
  },
);

export interface IEventsPaginatedResponse {
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  events: IEvent[];
}

export interface FetchEventsPaginatedParams {
  token: string;
  page: number;
  pageSize?: number;
  status?: string[];
  search?: string;
  organizationAccount?: string;
  buildDateFrom?: string;
  buildDateTo?: string;
  startDateFrom?: string;
  startDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
  decommissionDateFrom?: string;
  decommissionDateTo?: string;
}

export const fetchEventsPaginated = createAsyncThunk(
  'events/fetchPaginated',
  async ({
    token,
    page,
    pageSize = 50,
    status,
    search,
    organizationAccount,
    buildDateFrom,
    buildDateTo,
    startDateFrom,
    startDateTo,
    endDateFrom,
    endDateTo,
    decommissionDateFrom,
    decommissionDateTo,
  }: FetchEventsPaginatedParams): Promise<IEventsPaginatedResponse> => {
    const { dataopsAdminAppApiEndpoint } = getConfig();
    const url = `${dataopsAdminAppApiEndpoint}/event_management/events-paginated`;

    try {
      if (!token) throw new Error('Token not provided');

      const params: Record<string, string | number | string[]> = {
        page,
        page_size: pageSize,
      };

      if (status && status.length > 0) {
        params.status = status;
      }

      if (search) {
        params.search = search;
      }

      if (organizationAccount) {
        params.organization_account = organizationAccount;
      }

      if (buildDateFrom) {
        params.build_date_from = buildDateFrom;
      }

      if (buildDateTo) {
        params.build_date_to = buildDateTo;
      }

      if (startDateFrom) {
        params.start_date_from = startDateFrom;
      }

      if (startDateTo) {
        params.start_date_to = startDateTo;
      }

      if (endDateFrom) {
        params.end_date_from = endDateFrom;
      }

      if (endDateTo) {
        params.end_date_to = endDateTo;
      }

      if (decommissionDateFrom) {
        params.decommission_date_from = decommissionDateFrom;
      }

      if (decommissionDateTo) {
        params.decommission_date_to = decommissionDateTo;
      }

      const response = await axios.get(`${url}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        paramsSerializer: (p) => {
          const searchParams = new URLSearchParams();
          Object.entries(p).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach((v) => {
                searchParams.append(key, String(v));
              });
            } else {
              searchParams.append(key, String(value));
            }
          });
          return searchParams.toString();
        },
      });

      return response.data;
    } catch (error) {
      const errorMessage = (error as AxiosError).response?.data || 'Unknown error';
      console.error('Error fetching paginated events:', errorMessage);
      throw error;
    }
  },
);

const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchEvents.pending, (state) => {
      state.requestStatus = 'loading';
    });
    builder.addCase(fetchEvents.rejected, (state) => {
      state.requestStatus = 'failed';
    });
    builder.addCase(fetchEvents.fulfilled, (state, action) => {
      // Get all event slugs from the payload
      const payloadSlugs = action.payload.map((event) => event.slug);

      // Filter out events that are no longer in the payload (deleted events)
      state.events = state.events.filter((event) => payloadSlugs.includes(event.slug));

      // Update existing events or add new ones
      action.payload.forEach((newEvent) => {
        const existingEvent = state.events.find((e) => e.slug === newEvent.slug);
        if (existingEvent) {
          Object.assign(existingEvent, newEvent);
        } else {
          state.events.push(newEvent);
        }
      });

      state.requestStatus = 'succeeded';
    });
    builder.addCase(fetchEvent.pending, (state) => {
      state.requestStatus = 'loading';
    });
    builder.addCase(fetchEvent.rejected, (state) => {
      state.requestStatus = 'failed';
    });
    builder.addCase(fetchEvent.fulfilled, (state, action) => {
      const existingEvent = state.events.find((e) => e.slug === action.payload.slug);
      if (existingEvent) {
        Object.assign(existingEvent, action.payload);
      } else {
        state.events.push(action.payload);
      }
      state.requestStatus = 'succeeded';
    });
    builder.addCase(fetchEventAttendees.pending, (state) => {
      state.requestStatus = 'loading';
    });
    builder.addCase(fetchEventAttendees.rejected, (state) => {
      state.requestStatus = 'failed';
    });
    builder.addCase(fetchEventAttendees.fulfilled, (state, action) => {
      const event = state.events.find((e) => e.slug === action.meta.arg.slug);
      if (event) {
        event.eventAttendees = action.payload;
      }
      state.requestStatus = 'succeeded';
    });
    builder.addCase(fetchEventDetails.pending, (state) => {
      state.requestStatus = 'loading';
    });
    builder.addCase(fetchEventDetails.rejected, (state) => {
      state.requestStatus = 'failed';
    });
    builder.addCase(fetchEventDetails.fulfilled, (state, action) => {
      const existingEvent = state.events.find((e) => e.slug === action.payload.slug);
      if (existingEvent) {
        Object.assign(existingEvent, action.payload);
      } else {
        state.events.push(action.payload);
      }
      state.requestStatus = 'succeeded';
    });
    builder.addCase(fetchEventAccounts.pending, (state) => {
      state.requestStatus = 'loading';
    });
    builder.addCase(fetchEventAccounts.rejected, (state) => {
      state.requestStatus = 'failed';
    });
    builder.addCase(fetchEventAccounts.fulfilled, (state, action) => {
      const event = state.events.find((e) => e.slug === action.payload.slug);
      if (event) {
        // Store the accounts data in the event's child_accounts field
        (event as any).child_accounts = action.payload.accounts;
        // Optionally store pagination metadata
        (event as any).accounts_pagination = {
          total: action.payload.total,
          page: action.payload.page,
          page_size: action.payload.page_size,
          total_pages: action.payload.total_pages,
        };
      }
      state.requestStatus = 'succeeded';
    });
    builder.addCase(fetchAllEventAccounts.pending, (state) => {
      state.requestStatus = 'loading';
    });
    builder.addCase(fetchAllEventAccounts.rejected, (state) => {
      state.requestStatus = 'failed';
    });
    builder.addCase(fetchAllEventAccounts.fulfilled, (state, action) => {
      const event = state.events.find((e) => e.slug === action.payload.slug);
      if (event) {
        // Store all accounts data in the event's child_accounts field
        (event as any).child_accounts = action.payload.accounts;
      }
      state.requestStatus = 'succeeded';
    });
    builder.addCase(fetchSingleEventAccount.pending, (state) => {
      state.requestStatus = 'loading';
    });
    builder.addCase(fetchSingleEventAccount.rejected, (state) => {
      state.requestStatus = 'failed';
    });
    builder.addCase(fetchSingleEventAccount.fulfilled, (state, action) => {
      const event = state.events.find((e) => e.slug === action.payload.slug);
      if (event && (event as any).child_accounts) {
        // Find and update the specific account in the child_accounts array
        const accountIndex = (event as any).child_accounts.findIndex(
          (acc: IChildAccount) => acc.id === action.payload.account.id,
        );
        if (accountIndex !== -1) {
          // Update existing account
          (event as any).child_accounts[accountIndex] = action.payload.account;
        } else {
          // Add new account if it doesn't exist in the array
          (event as any).child_accounts.push(action.payload.account);
        }
      }
      state.requestStatus = 'succeeded';
    });
    builder.addCase(fetchEventsPaginated.pending, (state, action) => {
      if (action.meta.arg.page === 1) {
        state.paginatedRequestStatus = 'loading';
      } else {
        state.paginatedRequestStatus = 'loading-more';
      }
    });
    builder.addCase(fetchEventsPaginated.rejected, (state) => {
      state.paginatedRequestStatus = 'failed';
    });
    builder.addCase(fetchEventsPaginated.fulfilled, (state, action) => {
      if (action.meta.arg.page === 1) {
        state.paginatedEvents = action.payload.events;
      } else {
        state.paginatedEvents = [...state.paginatedEvents, ...action.payload.events];
      }
      state.paginatedTotal = action.payload.total;
      state.paginatedPage = action.payload.page;
      state.paginatedTotalPages = action.payload.total_pages;
      state.paginatedRequestStatus = 'succeeded';
    });
  },
});

// Selectors
export const selectEvents = (state: RootState) => state.events.events;
export const selectRequestStatus = (state: { events: IEventsState }) => state.events.requestStatus;
export const selectEventBySlug = (state: RootState, slug: string) =>
  state.events.events.find((event) => event.slug === slug);
export const selectPaginatedEvents = (state: RootState) => state.events.paginatedEvents;
export const selectPaginatedTotal = (state: RootState) => state.events.paginatedTotal;
export const selectPaginatedPage = (state: RootState) => state.events.paginatedPage;
export const selectPaginatedTotalPages = (state: RootState) => state.events.paginatedTotalPages;
export const selectPaginatedRequestStatus = (state: RootState) => state.events.paginatedRequestStatus;

export default eventsSlice.reducer;
